import React from 'react';
import Button, { ButtonStyle } from '../../giveinkind/components/Button';
import Checkbox from '../../giveinkind/components/Checkbox';
import TextBox, { TextBoxElement } from '../../giveinkind/components/TextBox';
import Organizer from '../../../models/inkind-organizers/Organizer';
import OrganizerInvitation from '../../../models/inkind-organizers/OrganizerInvitation';
import InKindOrganizersAPI from '../../../api/inkind-organizers/InkindOrganizersAPI';
import { CommonInputValidators } from '../../giveinkind/components/input-validator/CommonInputValidators';

export interface IInviterSectionProps {
  pageOwner: Organizer;
  pageOrganizers: Organizer[];
  pageInvitees: Organizer[];
  routeId: string;
  onSendInvitation: () => void;
}

export default function InviteSection({
  pageOwner,
  pageOrganizers,
  pageInvitees,
  routeId,
  onSendInvitation,
}: React.PropsWithChildren<IInviterSectionProps>): JSX.Element {
  const [emailAddress, setEmailAddress] = React.useState<string>('');
  const [canEdit, setCanEdit] = React.useState<boolean>(true);
  const [notifications, setNotifications] = React.useState<boolean>(true);
  const [isInputValid, setInputValid] = React.useState<boolean>(false); // TODO move logic to textbox validation HOC

  const textBoxRef = React.useRef<TextBoxElement>(null);

  const sendInvitation = async (invitation: OrganizerInvitation): Promise<void> => {
    const organizersApi: InKindOrganizersAPI = new InKindOrganizersAPI();
    await organizersApi.inviteUserAsPageOrganizer(routeId, invitation);
  };

  const onClickInvite = async function (e: React.MouseEvent) {
    e.preventDefault();

    if (isInputValid) {
      await sendInvitation({
        emailAddress,
        canEdit,
        notifications,
      });

      onSendInvitation();

      setEmailAddress('');
      setCanEdit(true);
      setNotifications(true);

      textBoxRef.current?.clearValidation();
    }
  };

  return (
    <div className="invite-section">
      <p className="invite-section-title">Invite others to help organize this page and receive notifications:</p>

      <section className="invite-section-input-wrapper">
        <TextBox
          ref={textBoxRef}
          type="email"
          className="form-control invite-organizer"
          placeholder="Email Address"
          onChange={setEmailAddress}
          value={emailAddress}
          errorValidators={() => [
            CommonInputValidators.getRequiredStringFieldValidator(),
            CommonInputValidators.getValidEmailAddressFieldValidator(),
            {
              validate: value =>
                pageOwner?.email.localeCompare(value) !== 0 &&
                pageOrganizers?.every(organizer => organizer.email.localeCompare(value) !== 0),
              getMessage: () => 'This e-mail address already belongs an organizer of this page.',
            },
            {
              validate: value => pageInvitees?.every(organizer => organizer.email.localeCompare(value) !== 0),
              getMessage: () => 'This e-mail address has already been invited to be an organizer of this page.',
            },
          ]}
          onValidityChange={setInputValid}
        ></TextBox>
      </section>

      <section className="options-section">
        <div>
          <Checkbox value={canEdit} onChange={value => setCanEdit(value)} content="Can edit page"></Checkbox>
        </div>
        <div>
          <Checkbox
            value={notifications}
            onChange={value => setNotifications(value)}
            content="Email All notifications"
          ></Checkbox>
        </div>
      </section>

      <section className="invite-section-button-wrapper">
        <Button
          text="Invite organizers"
          style={ButtonStyle.Save}
          disabled={!isInputValid}
          onClick={onClickInvite}
        ></Button>
      </section>
    </div>
  );
}
